/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import {
  Avatar,
  alpha,
  ClickAwayListener,
  Fade,
  IconButton,
  Paper,
  Popper,
  type Theme,
} from '@mui/material';
import { AppSystemPermissions } from '@poweralpha/common';
import { useRef, useState } from 'react';
import { usePermissions } from '../auth';
import { useDesktopActions } from '../desktop/DesktopContext';
import { useOpenSettings } from '../desktop/settings/useOpenSettings';
import { UserMenuPanel } from './UserMenuPanel';

const paperSx = (theme: Theme) => ({
  mt: 1,
  width: 300,
  borderRadius: '16px',
  border: `1px solid ${theme.palette.divider}`,
  bgcolor: alpha(theme.palette.background.paper, 0.92),
  backdropFilter: 'blur(28px) saturate(180%)',
  boxShadow: `0 22px 52px ${alpha('#000', theme.palette.mode === 'dark' ? 0.45 : 0.18)}`,
});

export const UserMenu = ({
  userName,
  userEmail,
  onLogout,
}: Readonly<{ userName: string; userEmail?: string; onLogout: () => void }>) => {
  const anchorEl = useRef<HTMLButtonElement>(null);
  const permissions = usePermissions();
  const { openPath } = useDesktopActions();
  const openSettings = useOpenSettings();
  const [open, setOpen] = useState(false);

  const showSystemAlerts = permissions?.includes(AppSystemPermissions.SYSTEM_ADMIN) ?? false;
  const nameParts = userName.split(' ').filter(Boolean);
  const userInitials = `${nameParts[0]?.[0] ?? ''}${nameParts[1]?.[0] ?? ''}`;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <span>
        <IconButton
          aria-label="user-menu"
          id="user-button"
          ref={anchorEl}
          aria-controls={open ? 'user-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
          size="small"
          onClick={() => setOpen((prev) => !prev)}
        >
          {/* Yellow initials badge from the v4 mockups (`.avatar`). */}
          <Avatar
            sx={(theme) => ({
              width: 26,
              height: 26,
              fontSize: 11,
              fontWeight: 700,
              color: '#0B1020',
              backgroundColor: '#FDE68A',
              outline: open ? `2px solid ${theme.palette.primary.main}` : 'none',
              outlineOffset: 2,
            })}
          >
            {userInitials}
          </Avatar>
        </IconButton>
        <Popper
          id="user-menu"
          open={open}
          anchorEl={anchorEl.current}
          transition
          placement="bottom-end"
          onClick={(e) => e.stopPropagation()}
          sx={{ zIndex: 2000 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={200}>
              <Paper elevation={0} sx={paperSx}>
                <UserMenuPanel
                  userName={userName}
                  userEmail={userEmail}
                  userInitials={userInitials}
                  showSystemAlerts={showSystemAlerts}
                  onOpenSettings={(tab) => {
                    setOpen(false);
                    openSettings(tab);
                  }}
                  onOpenPath={(path) => {
                    setOpen(false);
                    openPath(path);
                  }}
                  onClose={() => setOpen(false)}
                  onLogout={onLogout}
                />
              </Paper>
            </Fade>
          )}
        </Popper>
      </span>
    </ClickAwayListener>
  );
};
